<template>
  <v-dialog v-model="showForm" scrollable max-width="780px" @click:outside="onCancelClick">
    <template v-slot:activator="{ on, attrs }">
      <!--addXLButton-->
      <v-btn v-if="buttonType === 'addXLButton'" color="primary" rounded class="elevation-0 monospace-font white--text" v-bind="attrs" v-on="on"><v-icon>add</v-icon>{{ $t("documents.addNote") }}</v-btn>
      <!--editButton-->
      <v-btn v-if="buttonType === 'editButton'" elevation="0" icon x-small class="ms-1" color="grey lighten-1" v-bind="attrs" v-on="on"><v-icon small>edit</v-icon></v-btn>
    </template>
    <v-card>
      <v-card-title class="secondary white--text d-flex align-center justify-space-between">
        <span class="header-font" style="font-size: 22px;">
          <v-icon style="font-size: 18px;" color="white" class="mr-2">add</v-icon>
          {{ this.$t('documents.addNote') }}
        </span>
      </v-card-title>
      <v-card-text>
        <!--For Dallas-->
        <v-row class="py-0 mt-5" v-if="isDallas">
          <!--Type-->
          <v-col cols="12" :md="quickNote.typeId === 9 ? 6 : 12" class="py-0">
            <p class="body-1 font-weight-bold primary--text mb-0">
              {{ $t('general.type') }}
              <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="isEditing">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon class="ms-1" x-small color="secondary" v-on="on" v-bind="attrs">info</v-icon>
                </template>
                <span class="black--text body-1">{{ $t('disabledTooltips.notEditable') }}</span>
              </v-tooltip>
            </p>
            <v-autocomplete dense outlined clearable item-value="id" item-text="code" :class="$vuetify.breakpoint.xs ? 'mb-2' : 'me-2'"
              :disabled="isEditing"
              class="slim-input"
              v-model="quickNote.typeId"
              :items="documentTypes"
              :hint="$t('scoutingCalendar.tasks.accommodation.required')" :persistent-hint="!quickNote.typeId"
            >
              <template v-slot:selection="{item}">
                <v-card-text class="pa-0 primary--text font-weight-medium body-1">
                  <v-icon class="me-2" small>{{ getDocumentIconById(item.id) }}</v-icon>{{ $t(`documents.types.${item.code}`) }}
                </v-card-text>
              </template>
              <template v-slot:item="{item}"><span><v-icon class="me-2" small>{{ getDocumentIconById(item.id) }}</v-icon>{{ $t(`documents.types.${item.code}`) }}</span></template>  
            </v-autocomplete>
          </v-col>
           <!--Rating-->
          <v-col cols="12" md="6" class="py-0" v-if="quickNote.typeId === 9">
            <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('documents.rating') }}</p>
            <v-autocomplete dense outlined clearable item-value="id" item-text="name" :class="$vuetify.breakpoint.xs ? 'mb-2' : ''"
              class="slim-input"
              v-model="quickNote.overallScore"
              :items="overallRatingItems"
            >
              <template v-slot:selection="{item}"><v-card-text class="pa-0 primary--text font-weight-medium body-1">{{ item }}</v-card-text></template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <!--Notes-->
        <p class="body-1 font-weight-bold primary--text mb-0 mt-3">{{ $t('general.notes') }}</p>
        <div class="d-flex align-start justify-space-between mt-1">
          <v-textarea counter outlined :placeholder="$t('documents.notesPlaceholderAlt')" :class="$vuetify.breakpoint.xs ? 'mb-2' : 'flex-grow-1'"
            :rules="rules" 
            v-model="quickNote.notes">
          </v-textarea>
        </div>
      </v-card-text>
      <!--Buttons-->
      <v-card-actions class="d-flex justify-end pa-6">
        <v-btn  :color="$vuetify.theme.dark ? 'white' : 'secondary'"
          rounded outlined class="elevation-0 monospace-font px-4" @click="onCancelClick" :disabled="isSaving">{{$t('general.cancel')}}</v-btn>
        <v-btn rounded class="elevation-0 monospace-font white--text px-4" color="primary" @click="onSaveClick" :loading="isSaving" :disabled="!quickNote.notes.length || (quickNote.typeId === 9 && !quickNote.notes.length && !quickNote.overallScore)">{{$t('general.save')}}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

import * as _ from 'lodash';
import DocumentService from '../../../services/document.service';
import { getDocumentIconById } from '../../../utils/functions';

export default {
  name: 'AddQuickNote',
  props: {
    buttonType: {
      type: String,
      default: 'addXLButton' //Types: 'addXLButton', 'addXSButton', editButton
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    noteToUpdate: {
      type: Object,
      default: () => {
        return {};
      }
    },
    documentTypes: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: 'player'
    }
  },
  data() {
    return {
      showForm: false,
      isSaving: false,
      quickNote: { notes: '', typeId: 7 },
      rules: [(v) => v.length <= 1000 || this.$t('scoutingAnalysis.charactersMax')],
      overallRatingItems: ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3', 'D1', 'D2', 'D3', 'E1', 'E2', 'E3'],
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    darkMode(){
      return this.currentUser.darkMode || this.$vuetify.theme.dark;
    },
    isDallas() {
      return this.currentUser.clientId === 10400;
    }
  },
  created() {
    if (this.type === 'player' && this.$route.params.id) {
      this.quickNote.playerId = +this.$route.params.id;
    } else if (this.type === 'team' && this.$route.params.id) {
      this.quickNote.teamId = +this.$route.params.id;
    } else if (this.type === 'game' && this.$route.params.id) {
      this.quickNote.gameId = +this.$route.params.id;
    };

    //Editing
    if (this.isEditing) {
      this.quickNote = _.cloneDeep(this.noteToUpdate); //Keep original content in a different object
      this.quickNote.typeId = this.noteToUpdate.typeId;
    } 
  },
  methods: {
    onCancelClick() {
      //Reset quick note values
      this.quickNote = { notes: '', typeId: 7 };
      this.$emit("dialogClosed");
      this.isSaving = false;
      this.showForm = !this.showForm;
    },
    async onSaveClick() {
      this.isSaving = true;
      const postSearchEvent = { operation: "create", filename: "", documentType: this.quickNote.typeId === 9 ? "GENERAL_COMMENTS" : "QUICK_NOTES" };
      if (this.isEditing) {
        if (this.type === 'player') await DocumentService.updateDocument(this.quickNote);
        else if (this.type === 'team') await DocumentService.updateTeamDocument(this.quickNote);
        else if (this.type === 'game') await DocumentService.updateGameDocument(this.quickNote);
        postSearchEvent.operation = "update";
      } else {
        if (this.type === 'player') await DocumentService.postDocument(this.quickNote);
        else if (this.type === 'team') await DocumentService.postTeamDocument(this.quickNote);
        else if (this.type === 'game') await DocumentService.postGameDocument(this.quickNote);
      };
      this.$emit("postSearch", postSearchEvent);
      this.onCancelClick();
    },
    getDocumentIconById(id) {
      return getDocumentIconById(id);
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/views/scouting-analysis/quick-notes/AddQuickNote.vue